import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { useCoordination } from '@atlassiansox/engagekit-ts';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer -- the SpotlightCard is controlled by Engagement Platform's coordination client
import { SpotlightCard as OldSpotlightCard, SpotlightPulse } from '@atlaskit/onboarding';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	SpotlightSecondaryAction,
} from '@atlaskit/spotlight';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { useCoordinationClient } from '@confluence/engagement-provider';
import { fg } from '@confluence/feature-gating';

import {
	CHANGEBOARDING_LEARN_MORE_LINK,
	PAGE_OWNERSHIP_EP_MESSAGE,
} from './contentOwnershipConstants.ts';
import { i18n } from './i18n.ts';
import { useIsAllowedToChangeOwner } from './useIsAllowedToChangeOwner.ts';
import {
	CustomHeading,
	OnboardingContainer,
	StyledSpotlightCardWrapper,
} from './ContentOwnershipStyles.tsx';

const SPOTLIGHT_TARGET_NAME = 'change-owner-spotlight';

type ContentTogglingSpotlightProps = {
	children: React.ReactNode;
	ownedByAccountId: string | null;
};
export const ChangeOwnerSpotlightWrapper = ({
	children,
	ownedByAccountId,
}: ContentTogglingSpotlightProps) => {
	const coordinationClient = useCoordinationClient();
	const [started, stop] = useCoordination(coordinationClient, PAGE_OWNERSHIP_EP_MESSAGE);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const [isOnboardingActive, setIsOnboardingActive] = useState(false);
	const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();
	const showChangeOwnerAction = useIsAllowedToChangeOwner(ownedByAccountId);
	const shouldShowOnboarding =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR && started && showChangeOwnerAction && !isEmbeddedConfluence;

	useEffect(() => {
		if (shouldShowOnboarding) {
			setIsOnboardingActive(true);
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					actionSubject: 'feature',
					action: 'exposed',
					actionSubjectId: 'page.owner.changeboarding.spotlight',
					source: 'ChangeOwnerSpotlightWrapper',
				},
			}).fire();
		} else {
			// This is intended to handle the case where the call to
			// isEmbeddedConfluence_DO_NOT_USE() may return false the
			// first time and then return true.
			setIsOnboardingActive(false);
		}
	}, [shouldShowOnboarding, createAnalyticsEvent]);

	const endOnboarding = () => {
		void stop();
		setIsOnboardingActive(false);
	};

	if (!isOnboardingActive) {
		return <>{children}</>;
	}

	return (
		<OnboardingContainer>
			{fg('page_experiences_onboarding_to_spotlight_migration') ? (
				<>
					<PopoverProvider>
						<PopoverTarget>{children}</PopoverTarget>
						<PopoverContent
							dismiss={endOnboarding}
							isVisible={isOnboardingActive}
							placement="bottom-start"
							strategy="absolute"
						>
							<SpotlightCard key={SPOTLIGHT_TARGET_NAME} testId="change-owner-spotlight">
								<SpotlightHeader>
									<SpotlightHeadline>
										<CustomHeading>
											{intl.formatMessage(i18n.changeOwnerSpotlightTitle)}
										</CustomHeading>
									</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl onClick={endOnboarding} />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightBody>
									<FormattedMessage {...i18n.changeOwnerSpotlightBody} />
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightActions>
										<SpotlightSecondaryAction
											onClick={() => {
												window.open(CHANGEBOARDING_LEARN_MORE_LINK, '_blank');
											}}
										>
											{intl.formatMessage(i18n.changeOwnerSpotlightLearnMoreButton)}
										</SpotlightSecondaryAction>
										<SpotlightPrimaryAction onClick={endOnboarding}>
											{intl.formatMessage(i18n.changeOwnerSpotlightOkButton)}
										</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						</PopoverContent>
					</PopoverProvider>
				</>
			) : (
				<>
					<SpotlightPulse radius={3}>{children}</SpotlightPulse>
					<StyledSpotlightCardWrapper>
						<OldSpotlightCard
							key={SPOTLIGHT_TARGET_NAME}
							width={290}
							heading={
								<CustomHeading>{intl.formatMessage(i18n.changeOwnerSpotlightTitle)}</CustomHeading>
							}
							actions={[
								{
									onClick: endOnboarding,
									text: intl.formatMessage(i18n.changeOwnerSpotlightOkButton),
								},
								{
									onClick: () => {
										window.open(CHANGEBOARDING_LEARN_MORE_LINK, '_blank');
									},
									text: intl.formatMessage(i18n.changeOwnerSpotlightLearnMoreButton),
									appearance: 'subtle',
								},
							]}
						>
							<FormattedMessage {...i18n.changeOwnerSpotlightBody} />
						</OldSpotlightCard>
					</StyledSpotlightCardWrapper>
				</>
			)}
		</OnboardingContainer>
	);
};
